import React, { useState, forwardRef, useImperativeHandle } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MoreHorizOutlinedIcon from '@material-ui/icons/MoreHorizOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import ClearIcon from '@material-ui/icons/Clear';
// @ts-ignore
import { useTable, useSortBy, useExpanded } from 'react-table';
import { Chip, Table, TableBody, TableCell, TableRow } from '@material-ui/core';
import { EeMenu } from 'components/EeMenu/EeMenu';
import { EeModal } from 'components/EeModal/EeModal';
import { Link } from 'react-router-dom';

export interface TemplatesTableProps {
  templateRows: {}[];
  companiesByTemplateId: {};
  onRowEdit?: ({ row }: any) => void;
  onRowDuplicate?: ({ row }: any) => void;
  onRowDelete?: ({ row }: any) => void;
}

export const TemplatesTable: React.FC<TemplatesTableProps> = forwardRef(
  (
    {
      templateRows,
      companiesByTemplateId,
      onRowEdit,
      onRowDuplicate,
      onRowDelete,
    },
    ref,
  ) => {
    useImperativeHandle(ref, () => ({ toggleRowExpanded, rows }));
    const columns = React.useMemo(
      () => [
        {
          id: 'expander', // Make sure it has an ID
          Cell: ({ row }: any) =>
            row.canExpand ? (
              <ExpandMoreIcon
                {...row.getToggleRowExpandedProps({
                  style: {
                    transition: 'transform 0.2s ease',
                    ...(row.isExpanded ? {} : { transform: 'rotate(-90deg)' }),
                  },
                })}
              />
            ) : null,
        },
        {
          accessor: 'name',
          Cell: ({ row, value }: any) => {
            return row.canExpand ? (
              <span style={{ fontSize: 21, fontWeight: 600 }}>{value}</span>
            ) : (
              <Chip label={value} />
            );
          },
        },
        {
          accessor: 'group',
        },
        {
          accessor: 'type',
        },
        {
          accessor: 'description',
        },
      ],
      [],
    );

    const {
      getTableProps,
      getTableBodyProps,
      rows,
      prepareRow,
      toggleRowExpanded,
    } = useTable(
      { columns, data: templateRows, autoResetExpanded: false },
      useSortBy,
      useExpanded,
    );
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const templateCompanies = selectedRow
      ? // @ts-ignore
        companiesByTemplateId[selectedRow.original.id] || []
      : [];

    return (
      <>
        <Table {...getTableProps()} className="ee-table">
          <TableBody {...getTableBodyProps({ className: 'ee-table-thead' })}>
            {rows.map((row: any) => {
              prepareRow(row);
              const cellStyle = row.canExpand
                ? { backgroundColor: '#fff' }
                : {};
              return (
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell: any, cellIndex: number) => {
                    return (
                      <TableCell
                        variant="body"
                        {...cell.getCellProps({
                          style: cellStyle,
                          ...(cellIndex === 0 ? { width: 48 } : { height: 48 }),
                        })}
                        onClick={() =>
                          cellIndex !== 0 && toggleRowExpanded(row.id)
                        }
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    );
                  })}
                  {row.canExpand ? (
                    <TableCell
                      variant="body"
                      style={{ ...cellStyle, width: 48 }}
                    >
                      <EeMenu
                        onSelect={(item) => {
                          switch (item.id) {
                            case 'edit': {
                              // @ts-ignore
                              onRowEdit({ row });
                              break;
                            }
                            case 'duplicate': {
                              // @ts-ignore
                              onRowDuplicate({ row });
                              break;
                            }
                            case 'delete': {
                              setOpenDeleteConfirmation(true);
                              setSelectedRow(row);
                              break;
                            }
                          }
                        }}
                        // @ts-ignore
                        icon={<MoreHorizOutlinedIcon />}
                        buttonStyle={{ padding: 8 }}
                        items={[
                          {
                            id: 'edit',
                            // @ts-ignore
                            name: (
                              <>
                                <EditOutlinedIcon />
                                <span className="ml-2">Edit Template</span>
                              </>
                            ),
                          },
                          {
                            id: 'duplicate',
                            // @ts-ignore
                            name: (
                              <>
                                <FileCopyOutlinedIcon />
                                <span className="ml-2">Duplicate Template</span>
                              </>
                            ),
                          },
                          {
                            id: 'delete',
                            // @ts-ignore
                            name: (
                              <>
                                <ClearIcon />
                                <span className="ml-2">Delete Template</span>
                              </>
                            ),
                          },
                        ]}
                      />
                    </TableCell>
                  ) : (
                    <TableCell variant="body" />
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {selectedRow && (
          <EeModal
            open={openDeleteConfirmation}
            title={`Delete Template ${selectedRow.original.name}`}
            submitTitle={templateCompanies?.length > 0 ? 'Okay' : 'Delete'}
            onClose={() => setOpenDeleteConfirmation(false)}
            onSubmit={() => {
              if (templateCompanies?.length === 0)
                // @ts-ignore
                onRowDelete({ row: selectedRow });
              setOpenDeleteConfirmation(false);
            }}
          >
            {templateCompanies.length > 0 ? (
              <div>
                <p>
                  If you want to delete the{' '}
                  <strong>{selectedRow.original.name}</strong> Template, please
                  remove it from the following companies first:
                </p>
                <ol>
                  {templateCompanies.map((company: any) => (
                    <li key={company.id}>
                      <Link to="/kpis/company">{company.name}</Link>
                    </li>
                  ))}
                </ol>
              </div>
            ) : (
              <p className="p-3 mb-0">
                Do you want to delete the{' '}
                <strong>{selectedRow.original.name}</strong> template?
              </p>
            )}
          </EeModal>
        )}
      </>
    );
  },
);
